import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Modal } from 'flowbite';
import { IBasicTemporalPass, IFare, IPromotionalCode, ITemporalPass } from 'src/app/models';
import { FareType, UserRole } from 'src/app/models/enums';
import { FaresService } from 'src/app/services/fares/fares.service';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import { PromotionalCodesTabComponent } from './promotional-codes-tab/promotional-codes-tab.component';
import { TemporalPassesTabComponent } from './temporal-passes-tab/temporal-passes-tab.component';

export type FareModalTab = 'fares' | 'promotionalCodes' | 'temporalPasses';

@Component({
    selector: 'app-modal-fare',
    templateUrl: './modal-fare.component.html',
    styleUrl: './modal-fare.component.scss',
    standalone: false
})
export class ModalFareComponent implements OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() fares: IFare[] = [];
  @Input() selectedFare: IFare | undefined;
  @Input() routeId: number;
  @Input() clientId: number;
  @Input() userRole: UserRole;

  @Input() promotionalCodes: IPromotionalCode[] = [];
  @Input() selectedPromotionalCode: IPromotionalCode | undefined;
  @Input() temporalPasses: IBasicTemporalPass[] = [];
  @Input() selectedTemporalPass: IBasicTemporalPass | undefined;

  @Output() action = new EventEmitter<any>();
  @Output() deleteFareEvent = new EventEmitter<IFare>();
  @Output() loadMoreFares = new EventEmitter<any>();
  @Output() searchFares = new EventEmitter<any>();
  @Output() activateFareEvent = new EventEmitter<IFare>();
  @Output() promotionalCodeAction = new EventEmitter<any>();
  @Output() deletePromotionalCodeEvent = new EventEmitter<IPromotionalCode>();
  @Output() activatePromotionalCodeEvent = new EventEmitter<IPromotionalCode>();
  @Output() temporalPassAction = new EventEmitter<any>();
  @Output() deleteTemporalPassEvent = new EventEmitter<IBasicTemporalPass>();
  @Output() activateTemporalPassEvent = new EventEmitter<IBasicTemporalPass>();

  @ViewChild(PromotionalCodesTabComponent) promotionalCodesTab?: PromotionalCodesTabComponent;
  @ViewChild(TemporalPassesTabComponent) temporalPassesTab?: TemporalPassesTabComponent;

  public images = images;
  public FareType = FareType;
  public UserRole = UserRole;

  public activeTab: FareModalTab = 'fares';

  public isCreating = false;
  public isEditing = false;
  public pendingDeactivate = false;

  public editingFare: IFare = this.getEmptyFare();
  public faresValues: any[] = [];

  constructor(
    public literalService: LiteralService,
    private faresService: FaresService
  ) {}

  setActiveTab(tab: FareModalTab) {
    this.activeTab = tab;
    this.pendingDeactivate = false;
  }

  resetState(): void {
    this.activeTab = 'fares';
    this.isCreating = false;
    this.isEditing = false;
    this.pendingDeactivate = false;
    this.editingFare = this.getEmptyFare();
    this.promotionalCodesTab?.resetState();
    this.temporalPassesTab?.resetState();
  }

  getDeactivateTextKey(): string {
    if (this.activeTab === 'fares') return 'services.routes.fare.deactivate.text';
    if (this.activeTab === 'promotionalCodes') return 'services.routes.promotionalCode.deactivate.text';
    return 'services.routes.temporalPass.deactivate.text';
  }

  cancelPendingDeactivate() {
    this.pendingDeactivate = false;
  }

  confirmPendingDeactivate() {
    this.pendingDeactivate = false;
    if (this.activeTab === 'fares') this.onDelete();
    else if (this.activeTab === 'promotionalCodes') this.promotionalCodesTab?.onDelete();
    else if (this.activeTab === 'temporalPasses') this.temporalPassesTab?.onDelete();
  }

  showHeaderActions(): boolean {
    if (this.activeTab === 'fares') {
      return !this.isCreating && !this.isEditing && !!this.editingFare.id;
    }
    if (this.activeTab === 'promotionalCodes') {
      const tab = this.promotionalCodesTab;
      return !!tab && !tab.isCreating && !tab.isEditing && !!tab.editingCode?.id;
    }
    if (this.activeTab === 'temporalPasses') {
      const tab = this.temporalPassesTab;
      return !!tab && !tab.isCreating && !tab.isEditing && !!tab.editingPass?.id;
    }
    return false;
  }

  isCurrentItemActive(): boolean {
    if (this.activeTab === 'fares') return this.editingFare.active !== false;
    if (this.activeTab === 'promotionalCodes') return !this.promotionalCodesTab?.isCurrentDeactivated();
    if (this.activeTab === 'temporalPasses') return !this.temporalPassesTab?.isCurrentDeactivated();
    return true;
  }

  showHeaderEdit(): boolean {
    return this.showHeaderActions() && this.isCurrentItemActive();
  }

  onHeaderEdit() {
    if (this.activeTab === 'fares') this.startEditing();
    else if (this.activeTab === 'promotionalCodes') this.promotionalCodesTab?.startEditing();
    else if (this.activeTab === 'temporalPasses') this.temporalPassesTab?.startEditing();
  }

  onHeaderDeactivate() {
    this.pendingDeactivate = true;
  }

  onHeaderActivate() {
    if (this.activeTab === 'fares' && this.editingFare.id) {
      this.activateFareEvent.emit(this.editingFare as IFare);
    } else if (this.activeTab === 'promotionalCodes') {
      const code = this.promotionalCodesTab?.editingCode;
      if (code?.id) this.activatePromotionalCodeEvent.emit(code);
    } else if (this.activeTab === 'temporalPasses') {
      const pass = this.temporalPassesTab?.editingPass;
      if (pass?.id) this.activateTemporalPassEvent.emit(pass);
    }
  }

  getModalTitle(): string {
    if (this.activeTab === 'promotionalCodes') {
      return this.literalService.get(
        this.promotionalCodesTab?.isCreating
          ? 'services.routes.promotionalCode.createTitle'
          : 'services.routes.promotionalCode.title',
        true
      );
    }
    if (this.activeTab === 'temporalPasses') {
      return this.literalService.get(
        this.temporalPassesTab?.isCreating
          ? 'services.routes.temporalPass.createTitle'
          : 'services.routes.temporalPass.title',
        true
      );
    }
    return this.literalService.get(
      this.isCreating ? 'services.routes.fare.createTitle' : 'services.routes.fare.title',
      true
    );
  }

  getFareTypes(): any[] {
    return [
      { id: FareType.Basic, value: FareType.Basic, label: this.literalService.get('fareType.Basic', true) },
      { id: FareType.Flexible, value: FareType.Flexible, label: this.literalService.get('fareType.Flexible', true) },
      { id: FareType.Ranged_Flexible, value: FareType.Ranged_Flexible, label: this.literalService.get('fareType.Ranged_Flexible', true) }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fares']) {
      this.faresValues = this.getFaresForSelect();
    }
    if ((changes['selectedFare'] || changes['fares']) && this.selectedFare) {
      const fare = this.fares.find(f => f.id === this.selectedFare!.id);
      if (fare && fare.id && fare.type) {
        this.loadFullFare(fare.id, fare.type, fare);
      } else if (changes['selectedFare']) {
        this.editingFare = { ...this.selectedFare };
        if (!this.editingFare.priceRanges) {
          this.editingFare.priceRanges = [];
        }
      }
    }
  }

  private async loadFullFare(id: number, type: FareType, fallbackFare: IFare): Promise<void> {
    try {
      const fullFare = await this.faresService.getFare(id, type);
      this.editingFare = { ...fullFare, id, type };
      if (!this.editingFare.priceRanges) {
        this.editingFare.priceRanges = [];
      }
    } catch (error) {
      this.editingFare = { ...fallbackFare, id, type };
      if (!this.editingFare.priceRanges) {
        this.editingFare.priceRanges = [];
      }
    }
  }

  getEmptyFare(): IFare {
    return {
      id: undefined,
      name: undefined,
      client: undefined,
      services: undefined,
      type: FareType.Basic,
      active: true,
      sameDayValidity: true,
      basePrice: undefined,
      variablePrice: undefined,
      priceRanges: [],
      town: undefined,
      zones: undefined,
      zoneExchanges: undefined,
      defaultPrice: undefined
    };
  }

  startCreating() {
    this.isCreating = true;
    this.isEditing = false;
    this.editingFare = this.getEmptyFare();
  }

  startEditing() {
    if (this.editingFare.id) {
      this.isEditing = true;
      this.isCreating = false;
    }
  }

  cancelEdit() {
    this.isCreating = false;
    this.isEditing = false;
    if (this.editingFare.id && this.editingFare.type) {
      this.loadFullFare(this.editingFare.id, this.editingFare.type, this.editingFare);
    } else {
      this.editingFare = this.getEmptyFare();
    }
  }

  async selectFare(event: any) {
    const selectedId = event.options ? event.options[0]?.id : event.target?.value;
    const fare = this.fares.find(f => f.id === selectedId);
    if (fare && fare.id && fare.type) {
      await this.loadFullFare(fare.id, fare.type, fare);
      this.isCreating = false;
      this.isEditing = false;
    }
  }

  changeFareType(event: any) {
    const typeValue = event.options ? event.options[0]?.value : event.target?.value;
    this.editingFare.type = typeValue as FareType;
    if (typeValue !== FareType.Ranged_Flexible) {
      this.editingFare.priceRanges = [];
    }
    if (typeValue !== FareType.Flexible) {
      this.editingFare.variablePrice = undefined;
    }
  }

  updateName(event: any) {
    this.editingFare.name = event.target?.value || event;
  }

  updateBasePrice(event: any) {
    const value = event.target?.value || event;
    this.editingFare.basePrice = value ? parseFloat(value) : undefined;
  }

  updateVariablePrice(event: any) {
    const value = event.target?.value || event;
    this.editingFare.variablePrice = value ? parseFloat(value) : undefined;
  }

  addRange() {
    if (!this.editingFare.priceRanges) {
      this.editingFare.priceRanges = [];
    }
    this.editingFare.priceRanges.push({
      id: undefined,
      start: undefined,
      end: undefined,
      price: undefined
    });
  }

  removeRange(index: number) {
    if (this.editingFare.priceRanges) {
      this.editingFare.priceRanges.splice(index, 1);
    }
  }

  updateRangePrice(index: number, event: any) {
    const value = event.target?.value || event;
    if (this.editingFare.priceRanges && this.editingFare.priceRanges[index]) {
      this.editingFare.priceRanges[index].price = value ? parseFloat(value) : undefined;
    }
  }

  updateRangeStart(index: number, event: any) {
    const value = event.target?.value || event;
    if (this.editingFare.priceRanges && this.editingFare.priceRanges[index]) {
      this.editingFare.priceRanges[index].start = value ? parseFloat(value) : undefined;
    }
  }

  updateRangeEnd(index: number, event: any) {
    const value = event.target?.value || event;
    if (this.editingFare.priceRanges && this.editingFare.priceRanges[index]) {
      this.editingFare.priceRanges[index].end = value ? parseFloat(value) : undefined;
    }
  }

  onDelete() {
    if (this.editingFare.id) {
      this.deleteFareEvent.emit(this.editingFare as IFare);
    }
  }

  onSubmit() {
    const fareData = {
      ...this.editingFare,
      clientId: this.clientId,
      routeId: this.routeId,
    };

    const wasEditing = this.isEditing;
    const wasCreating = this.isCreating;

    this.action.emit({
      fare: fareData,
      isCreating: this.isCreating,
      isEditing: this.isEditing
    });

    if (!wasEditing && !wasCreating) {
      this.modal.hide();
    }
  }

  async onEditSuccess(fareId: number, fareType: FareType): Promise<void> {
    this.isEditing = false;
    this.isCreating = false;
    await this.loadFullFare(fareId, fareType, this.editingFare);
  }

  async onCreateSuccess(createdFare: IFare): Promise<void> {
    this.isCreating = false;
    this.isEditing = false;
    if (createdFare.id && createdFare.type) {
      await this.loadFullFare(createdFare.id, createdFare.type, createdFare);
    } else {
      this.editingFare = { ...createdFare };
    }
  }

  onDeleteSuccess(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.editingFare = this.getEmptyFare();
  }

  isFormValid(): boolean {
    if (!this.editingFare.name || !this.editingFare.type || this.editingFare.basePrice === undefined) {
      return false;
    }
    if (this.editingFare.type === FareType.Flexible && this.editingFare.variablePrice === undefined) {
      return false;
    }
    if (this.editingFare.type === FareType.Ranged_Flexible) {
      if (!this.editingFare.priceRanges || this.editingFare.priceRanges.length === 0) {
        return false;
      }
      for (const range of this.editingFare.priceRanges) {
        if (range.price === undefined || range.start === undefined || range.end === undefined) {
          return false;
        }
      }
    }
    return true;
  }

  getFaresForSelect(): any[] {
    const suffix = ' (' + this.literalService.get('services.routes.deactivated', true) + ')';
    return this.fares.map(f => ({
      id: f.id,
      value: f.id,
      label: f.active === false ? `${f.name}${suffix}` : f.name
    }));
  }

  formatPrice(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '';
    }
    return value.toFixed(2);
  }

  loadMoreFare(event: any) {
    this.loadMoreFares.emit(event);
  }

  searchFare(event: any) {
    this.searchFares.emit(event);
  }
  
  async onPromotionalCodeCreateSuccess(created: IPromotionalCode) {
    await this.promotionalCodesTab?.onCreateSuccess(created);
  }

  async onPromotionalCodeEditSuccess(updated: IPromotionalCode) {
    await this.promotionalCodesTab?.onEditSuccess(updated);
  }

  onPromotionalCodeDeleteSuccess() {
    this.promotionalCodesTab?.onDeleteSuccess();
  }

  async onTemporalPassCreateSuccess(created: ITemporalPass) {
    await this.temporalPassesTab?.onCreateSuccess(created);
  }

  async onTemporalPassEditSuccess(updated: ITemporalPass) {
    await this.temporalPassesTab?.onEditSuccess(updated);
  }

  onTemporalPassDeleteSuccess() {
    this.temporalPassesTab?.onDeleteSuccess();
  }
}
